import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BrandMark } from "../components/Brand";
import StatusBadge from "../components/StatusBadge";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { getAttorney, fetchAttorneyMe } from "../lib/attorneyAuth";
import { ArrowLeft, FileText, Upload, Trash2, FileDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DOC_CATEGORIES = [
  { v: "police_report", l: "Police report" },
  { v: "medical_records", l: "Medical records" },
  { v: "insurance", l: "Insurance info" },
  { v: "other", l: "Other" },
];

export default function AttorneyCaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const attorney = getAttorney();
  const [app, setApp] = useState(null);
  const [docName, setDocName] = useState("");
  const [docCat, setDocCat] = useState("medical_records");
  const [note, setNote] = useState("");

  useSEO(app ? `Case · ${app.name}` : "Case detail", "View and manage your case in the Instabridge attorney portal.");

  useEffect(() => {
    (async () => {
      try {
        await fetchAttorneyMe();
        await load();
      } catch {
        nav("/attorney-login", { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      setApp(data);
    } catch { toast.error("Could not load case"); }
  };

  const uploadDoc = async () => {
    if (!docName.trim()) return;
    await api.post(`/applications/${app.id}/documents`, {
      name: docName.trim().includes(".") ? docName.trim() : `${docName.trim()}.pdf`,
      category: docCat, uploaded_by: "attorney",
      size_kb: Math.floor(Math.random() * 1500) + 300,
    });
    setDocName("");
    toast.success("Document uploaded");
    load();
  };

  const removeDoc = async (docId) => {
    await api.delete(`/applications/${app.id}/documents/${docId}`);
    load();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/applications/${app.id}/notes`, { author: attorney.user.name, role: "attorney", content: note.trim() });
    setNote("");
    toast.success("Note added");
    load();
  };

  if (!app) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading…</div>;

  const canPayoff = app.status === "approved" || app.status === "funded";

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="atty-case-detail">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/attorney/portal"><BrandMark /></Link>
          <Link to="/attorney/portal"><Button variant="ghost" size="sm" data-testid="atty-back-portal"><ArrowLeft className="w-4 h-4 mr-2" />Portal</Button></Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="uppercase-label">Case</div>
              <h1 className="font-display text-3xl text-[#0A2540] font-semibold mt-1.5">{app.name}</h1>
              <div className="text-sm text-[#6B7280] mt-1">{app.email} · {app.phone}</div>
            </div>
            <div className="text-right">
              <StatusBadge status={app.status} />
              <div className="font-display text-3xl font-semibold text-[#0066FF] mt-3 tabular-nums">{formatCurrency(app.requested_amount)}</div>
              <div className="text-xs text-[#6B7280]">requested</div>
              {app.funding_recommendation != null && (
                <div className="text-sm text-[#047857] mt-2">Approved: {formatCurrency(app.funding_recommendation)}</div>
              )}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mt-7 pt-7 border-t border-[#E5E7EB]">
            <KV label="Accident type" value={app.accident_type} />
            <KV label="Accident date" value={formatDate(app.accident_date)} />
            <KV label="Submitted" value={formatDate(app.created_at)} />
            <KV label="Last updated" value={formatDate(app.updated_at)} />
            <KV label="Verification" value={app.attorney_verified ? "Verified" : "Pending"} />
          </div>

          {canPayoff && (
            <div className="mt-7 pt-7 border-t border-[#E5E7EB] flex items-center gap-3">
              <a href={`/attorney/portal/payoff/${app.id}`} target="_blank" rel="noopener noreferrer">
                <Button className="btn-primary-navy rounded-full" data-testid="open-payoff-btn"><FileDown className="w-4 h-4 mr-2" />Open payoff letter</Button>
              </a>
              <span className="text-xs text-[#6B7280]">Opens in new tab — print to PDF or save</span>
            </div>
          )}

          <div className="mt-7 pt-7 border-t border-[#E5E7EB]">
            <div className="uppercase-label mb-2">Injury description</div>
            <p className="text-[#374151] leading-relaxed">{app.injury_description}</p>
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display text-xl font-semibold text-[#0A2540] mb-4">Documents</h2>
          <div className="space-y-2">
            {app.documents.length === 0 && <div className="text-sm text-[#6B7280] italic">No documents yet.</div>}
            {app.documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-lg" data-testid={`atty-doc-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-[#0066FF] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0A2540] truncate">{d.name}</div>
                    <div className="text-xs text-[#6B7280]">{d.category.replace("_"," ")} · {(d.size_kb/1024).toFixed(2)} MB · {d.uploaded_by}</div>
                  </div>
                </div>
                {d.uploaded_by === "attorney" && (
                  <Button variant="ghost" size="sm" onClick={() => removeDoc(d.id)} data-testid={`atty-doc-del-${d.id}`}><Trash2 className="w-4 h-4 text-[#B91C1C]" /></Button>
                )}
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-4 mt-4 border-t border-[#E5E7EB]">
            <Input placeholder="File name (e.g., medical_record.pdf)" value={docName} onChange={(e) => setDocName(e.target.value)} data-testid="atty-doc-name-input" />
            <div className="flex gap-2">
              <Select value={docCat} onValueChange={setDocCat}>
                <SelectTrigger className="flex-1" data-testid="atty-doc-cat-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={uploadDoc} className="btn-primary-navy" data-testid="atty-doc-upload-btn"><Upload className="w-4 h-4 mr-2" />Upload</Button>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <h2 className="font-display text-xl font-semibold text-[#0A2540] mb-4">Notes</h2>
          <div className="space-y-3 mb-3">
            {app.notes.length === 0 && <div className="text-sm text-[#6B7280] italic">No notes yet.</div>}
            {app.notes.map((n) => (
              <div key={n.id} className="p-4 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1.5">
                  <span className="font-medium text-[#0A2540]">{n.author} · {n.role}</span>
                  <span>{formatDate(n.created_at)}</span>
                </div>
                <div className="text-sm text-[#374151] whitespace-pre-wrap">{n.content}</div>
              </div>
            ))}
          </div>
          <Textarea placeholder="Add a note for the underwriter…" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="atty-note-input" />
          <Button onClick={addNote} className="btn-primary-navy mt-2" data-testid="atty-note-add">Add note</Button>
        </div>

        {app.signature_data && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold text-[#0A2540] mb-4">Verification signature</h2>
            <div className="flex items-center gap-2 text-sm text-[#047857] mb-3"><CheckCircle2 className="w-4 h-4" /> Verified by {app.signature_name}</div>
            <img src={app.signature_data} alt="Signature" className="border border-[#E5E7EB] rounded p-2 bg-[#F9FAFB] max-h-32" />
          </div>
        )}
      </div>
    </div>
  );
}

const KV = ({ label, value }) => (
  <div>
    <div className="uppercase-label mb-0.5">{label}</div>
    <div className="text-sm text-[#0A2540]">{value}</div>
  </div>
);
